import { attendanceModes, FIRST_CLASS_LABEL, resolveAnswers, sessionsLeftAfterAll, sessionsLeftView, type AttendanceMark, type AttendanceMode, type SessionsLeft } from "@teamna/shared";
import { useEffect, useMemo, useRef, useState, useSyncExternalStore, type CSSProperties, type PointerEvent, type ReactNode } from "react";
import { Avatar } from "./Avatar.tsx";
import { HealthNoteBadge } from "./HealthNoteBadge.tsx";
import { Badge } from "./Badge.tsx";
import { Button } from "./Button.tsx";
import { CheckInList, type CheckInListProps } from "./CheckInList.tsx";
import { SegmentedControl } from "./Choice.tsx";
import { ArrowLeftIcon, CheckIcon, CloseIcon } from "./Icons.tsx";
import { InlineError } from "./Notice.tsx";
import { NoticeCoin } from "./Section.tsx";
import { SessionsLeftBar } from "./SessionsLeftBar.tsx";

/**
 * Take attendance: the flow signed off on 2026-10-04 (Asana "Attendance: take attendance with
 * cards or a list", prototype `ai-personal-assistant/teamna/prototypes/attendance-signoff.html`).
 *
 * The person picks a way the first time, and can switch at the top at any time. The answers stay
 * when they switch.
 *
 * - Cards: one participant at a time, Yes or No (or swipe right or left). The next card comes by
 *   itself. Back and Next move between cards, and the progress bar has one step for each
 *   participant, in the colour of its answer. Tap a step to go to that participant.
 * - List: Yes or No next to each name. The row slides out to "Checked". Undo shows for 4 seconds,
 *   and "Change" in Checked puts a name back.
 *
 * When the last participant has an answer, the check screen opens by itself: "Everyone is
 * checked", the totals, Absent and Came. A tap on a name changes it. Save sends the answers, and
 * the saved screen follows. A session that already has every mark opens on the check screen.
 *
 * The route can put people with no booking on the register: on a group level, the level's swimmers
 * who did not book (2026-10-22, most swimmers at DR Sports Academy come with no booking). They are
 * rows like the others, with a `detail` line. What their answers store is the route's job.
 *
 * Where clients pick each class, the screen opens on check-in by name (I1, `checkIn`): one tap for
 * each client who comes, saved at once (`CheckInList`). Cards and List sit next to it at the top, for
 * the whole register. A family business gets no `checkIn` and keeps Cards and List as before.
 *
 * This component holds the screen state only. The route reads the session, keeps the chosen way on
 * the account, and saves (`onSave` resolves when the marks are stored).
 */

export type AttendancePerson = {
  bookingId: string;
  name: string;
  /** "First time on a Sunday", "Inhaler in his bag": what the staff member reads on the card. */
  note: string | null;
  /** A plain line under the name, from Teamna not from a person: "Not booked. Usually comes on Monday". */
  detail?: string | null;
  attendance?: AttendanceMark | null;
  /** The profile photo. Missing or null shows the initials. */
  photoUrl?: string | null;
  /** Set for a swimmer the coach added who came and was not booked: Remove takes them off. */
  extraId?: string | null;
  /** A yes on a health question (C2): the flag "Health note", never the answers. */
  healthNote?: boolean;
  /** The person's first class at the business (C3): a "First class" badge. */
  firstClass?: boolean;
  /** When the person was checked in by name (I1). */
  checkedInAt?: string | null;
  /** Absent, marked by Teamna 10 minutes after the start (I1). */
  autoAbsent?: boolean;
  /** A row with no booking (the level's swimmers who did not book): never on the check-in list. */
  notBooked?: boolean;
  /** The family's sessions left for this class: the dashed bar under the name (2026-10-29). Missing or null shows no bar. */
  sessionsLeft?: SessionsLeft | null;
};

/**
 * "Someone else came" (slice 2 of coaches add their swimmers): the button that opens the search,
 * the names that came and wait for a parent (no booking yet, so not in `people`), and Remove.
 */
export type AttendanceExtras = {
  waiting: Array<{ id: string; name: string }>;
  onAdd: () => void;
  onRemove: (extraId: string) => void;
};

export type AttendanceAnswers = Record<string, AttendanceMark>;

export type TakeAttendanceProps = {
  /** "Level 2 16:45". */
  title: string;
  /** "Main pool. Ended 17:30". */
  subtitle: string;
  people: AttendancePerson[];
  /** The way the person picked. Null asks them first. */
  mode: AttendanceMode | null;
  onModeChange: (mode: AttendanceMode) => void;
  /**
   * Store the answers. Resolves when they are saved; a rejection shows the error on the check screen.
   * "nothing" means there was nothing to store (nobody booked and nobody came), so the screen does
   * not say it saved.
   */
  onSave: (answers: AttendanceAnswers) => Promise<void | "nothing">;
  onExit: () => void;
  /** Where Back goes: "Today", "Schedule". */
  exitLabel: string;
  /** The business's word for a participant: "swimmer", "swimmers". */
  participantLabel: { one: string; many: string };
  /** The time now in the business clock, for "Attendance saved at 17:31". */
  formatNow: () => string;
  /**
   * True on a screen of its own (the staff route): the screen is at least as tall as the window,
   * so the save bar sits at the bottom of a short list. False inside the owner office, whose shell
   * already has a top bar and padding: the save bar then follows the list and sticks while it
   * scrolls, and the page never grows by the shell's height.
   */
  fillScreen?: boolean;
  /** Someone else came. Left out: the screen has no add button (older callers). */
  extras?: AttendanceExtras;
  /** A note over the register while people are checked, such as a list that could not be read. */
  notice?: ReactNode;
  /**
   * Check-in by name (I1), where clients pick each class: the screen opens on the check-in list,
   * with Cards and List next to it. Left out: Cards and List only, as before.
   */
  checkIn?: Omit<CheckInListProps, "people" | "participantLabel">;
};

/** The way on the switch: check-in, or the person's cards or list. */
type View = "checkin" | AttendanceMode;

type Step = "take" | "check" | "saved";
type Enter = "next" | "prev" | "fwd" | "jump";

const UNDO_MS = 4000;

function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}

function AnswerTag({ answer }: { answer: AttendanceMark | undefined }) {
  if (answer === "present") return <span className="rounded-full bg-confirmed-surface px-2.5 py-0.5 text-[12px] font-bold text-confirmed-ink">Came</span>;
  if (answer === "absent") return <span className="rounded-full bg-cancelled-surface px-2.5 py-0.5 text-[12px] font-bold text-cancelled-ink">Absent</span>;
  return <span className="rounded-full bg-sunken px-2.5 py-0.5 text-[12px] font-semibold text-ink-2">Not checked</span>;
}

const REDUCE_MOTION = "(prefers-reduced-motion: reduce)";

function subscribeReducedMotion(onChange: () => void): () => void {
  if (typeof window === "undefined" || !window.matchMedia) return () => {};
  const query = window.matchMedia(REDUCE_MOTION);
  query.addEventListener?.("change", onChange);
  return () => query.removeEventListener?.("change", onChange);
}

function readReducedMotion(): boolean {
  return typeof window !== "undefined" && Boolean(window.matchMedia?.(REDUCE_MOTION).matches);
}

/** True when the phone asks for less motion: the screen then skips its animation waits. */
function usePrefersReducedMotion(): boolean {
  return useSyncExternalStore(subscribeReducedMotion, readReducedMotion, () => false);
}

const CONFETTI = ["var(--color-confirmed)", "var(--color-accent)", "var(--color-late-gold)", "var(--color-cancelled)", "var(--color-accent-soft)"];

function Burst() {
  return (
    <span className="att-burst" aria-hidden="true">
      {Array.from({ length: 14 }, (_, index) => (
        <i
          key={index}
          style={
            {
              "--att-a": `${(index * 360) / 14}deg`,
              "--att-c": CONFETTI[index % CONFETTI.length],
              "--att-d": `${(index % 3) * 40}ms`,
            } as CSSProperties
          }
        />
      ))}
    </span>
  );
}

export function TakeAttendance({
  title,
  subtitle,
  people,
  mode,
  onModeChange,
  onSave,
  onExit,
  exitLabel,
  participantLabel,
  formatNow,
  fillScreen = true,
  extras,
  notice,
  checkIn,
}: TakeAttendanceProps) {
  const reduced = usePrefersReducedMotion();
  const [checkingIn, setCheckingIn] = useState(Boolean(checkIn));
  const total = people.length;

  // Only what this person changed on this screen. A participant they did not touch always shows
  // the mark as stored now, so a refetch brings in another device's change, and Save never sends
  // an old value back over it. `null` is a cleared answer (Undo, Change in Checked).
  const [edits, setEditsState] = useState<Record<string, AttendanceMark | null>>({});
  // The edits and the roster as they are right now. Every change goes through `setEdits`, which
  // writes the ref first, and the timers that finish an animation read the refs, never the render
  // they were scheduled from, so two quick taps can never drop the first answer.
  const editsRef = useRef(edits);
  const peopleRef = useRef(people);
  useEffect(() => {
    peopleRef.current = people;
  });
  const setEdits = (changes: Record<string, AttendanceMark | null>) => {
    editsRef.current = { ...editsRef.current, ...changes };
    setEditsState(editsRef.current);
  };
  /** The answers now: the person's own edits over the stored marks. */
  const current = () => resolveAnswers(editsRef.current, peopleRef.current);
  const answers = useMemo(() => resolveAnswers(edits, people), [edits, people]);
  const allMarkedAtStart = total > 0 && people.every((person) => person.attendance);
  const notBookedCount = extras ? people.filter((person) => person.extraId).length + extras.waiting.length : 0;
  // Check-in is a live list with no check step: it opens on the list, also with every mark made.
  const [step, setStep] = useState<Step>(allMarkedAtStart && !checkIn ? "check" : "take");
  const [celebrate, setCelebrate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [nothingStored, setNothingStored] = useState(false);
  const [announce, setAnnounce] = useState("");

  const firstOpenIndex = (from: AttendanceAnswers) => people.findIndex((person) => !from[person.bookingId]);
  const [index, setIndex] = useState(() => Math.max(0, firstOpenIndex(resolveAnswers({}, people))));
  const [enter, setEnter] = useState<Enter>("jump");
  const [cardKey, setCardKey] = useState(0);
  const [poppedStep, setPoppedStep] = useState<number | null>(null);

  // The roster can change under the screen (a booking made or cancelled while it is open). When a
  // participant with no answer appears, the check screen is no longer true: go back to the first
  // open one. This is React's "adjust state while rendering" pattern, not an effect, so the check
  // screen never paints with a booking that has no answer.
  const rosterKey = people.map((person) => person.bookingId).join("|");
  const [seenRoster, setSeenRoster] = useState(rosterKey);
  if (rosterKey !== seenRoster) {
    setSeenRoster(rosterKey);
    const open = people.findIndex((person) => !answers[person.bookingId]);
    if (open >= 0 && step === "check") {
      setStep("take");
      setCelebrate(false);
      setIndex(open);
      setEnter("jump");
      setCardKey((key) => key + 1);
    } else if (index > people.length - 1) {
      setIndex(Math.max(0, people.length - 1));
    }
  }

  // Rows that slide out of the list, each on its own clock, with the answer the person gave last.
  const [leaving, setLeavingState] = useState<Record<string, { answer: AttendanceMark; phase: "hit" | "leave" }>>({});
  const leavingRef = useRef(leaving);
  const setLeaving = (next: Record<string, { answer: AttendanceMark; phase: "hit" | "leave" }>) => {
    leavingRef.current = next;
    setLeavingState(next);
  };
  const [entering, setEntering] = useState<string | null>(null);
  const [undo, setUndo] = useState<{ id: string; answer: AttendanceMark } | null>(null);
  const [showChecked, setShowChecked] = useState(false);
  const [bumped, setBumped] = useState(0);

  // The card that is flying out, if any. A second tap while it flies changes that person's answer:
  // the last tap wins, and no tap is dropped.
  const flying = useRef<string | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x: number; dx: number; pointerId: number } | null>(null);
  const timers = useRef<number[]>([]);
  const wait = (ms: number) => (reduced ? 0 : ms);
  const later = (fn: () => void, ms: number) => {
    timers.current.push(window.setTimeout(fn, ms));
  };
  useEffect(() => {
    const pending = timers.current;
    return () => pending.forEach((id) => window.clearTimeout(id));
  }, []);

  useEffect(() => {
    if (!undo) return;
    const id = window.setTimeout(() => setUndo(null), UNDO_MS);
    return () => window.clearTimeout(id);
  }, [undo]);

  const counts = useMemo(() => {
    let present = 0;
    let absent = 0;
    for (const person of people) {
      if (answers[person.bookingId] === "present") present++;
      else if (answers[person.bookingId] === "absent") absent++;
    }
    return { present, absent, done: present + absent };
  }, [answers, people]);

  // The bar of each person after the answers, before Save. Siblings share the family's sessions.
  const leftAfter = useMemo(() => sessionsLeftAfterAll(people, answers), [people, answers]);

  const people1 = (count: number) => `${count} ${count === 1 ? participantLabel.one : participantLabel.many}`;
  const said = (person: AttendancePerson, answer: AttendanceMark) => `${firstName(person.name)}: ${answer === "present" ? "came" : "absent"}`;

  function finish(next: AttendanceAnswers) {
    if (peopleRef.current.every((person) => next[person.bookingId])) {
      setStep("check");
      setCelebrate(true);
      setUndo(null);
      return true;
    }
    return false;
  }

  /**
   * The only way into the check screen besides `finish`. It opens only when every participant on
   * the roster as it is now has an answer; otherwise the person goes to the first open one.
   */
  function openCheck() {
    const now = current();
    const open = people.findIndex((person) => !now[person.bookingId]);
    if (open >= 0) {
      setStep("take");
      setCelebrate(false);
      goTo(open, "jump");
      return;
    }
    setStep("check");
  }

  function goTo(nextIndex: number, how: Enter) {
    setIndex(nextIndex);
    setEnter(how);
    setCardKey((key) => key + 1);
  }

  // --- cards ---------------------------------------------------------------

  function answerCard(answer: AttendanceMark) {
    if (flying.current) {
      // The card is still on its way out: this tap changes that same person's answer.
      const id = flying.current;
      setEdits({ [id]: answer });
      if (cardRef.current) cardRef.current.dataset.lean = answer;
      const person = people.find((entry) => entry.bookingId === id);
      if (person) setAnnounce(said(person, answer));
      return;
    }
    const person = people[index];
    if (!person) return;
    flying.current = person.bookingId;
    setEdits({ [person.bookingId]: answer });
    setPoppedStep(index);
    setAnnounce(said(person, answer));

    const card = cardRef.current;
    if (card && !reduced) {
      card.removeAttribute("data-dragging");
      card.dataset.lean = answer;
      card.style.transition = "transform 0.36s cubic-bezier(0.3, 0.6, 0.4, 1), opacity 0.36s ease";
      requestAnimationFrame(() => {
        card.style.transform = `translate(${answer === "present" ? 150 : -150}%, -28px) rotate(${answer === "present" ? 22 : -22}deg)`;
        card.style.opacity = "0";
      });
    }

    later(() => {
      flying.current = null;
      const now = current();
      if (finish(now)) return;
      if (index < total - 1) goTo(index + 1, "next");
      else goTo(Math.max(0, firstOpenIndex(now)), "jump");
    }, wait(360));
  }

  function onPointerDown(event: PointerEvent<HTMLDivElement>) {
    if (flying.current || (event.target as HTMLElement).closest("button")) return;
    drag.current = { x: event.clientX, dx: 0, pointerId: event.pointerId };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    event.currentTarget.dataset.dragging = "";
  }
  function onPointerMove(event: PointerEvent<HTMLDivElement>) {
    const state = drag.current;
    if (!state || state.pointerId !== event.pointerId) return;
    state.dx = event.clientX - state.x;
    const card = event.currentTarget;
    card.style.transform = `translateX(${state.dx}px) rotate(${state.dx / 16}deg)`;
    card.dataset.lean = state.dx > 40 ? "present" : state.dx < -40 ? "absent" : "";
  }
  function onPointerEnd(event: PointerEvent<HTMLDivElement>) {
    const state = drag.current;
    if (!state || state.pointerId !== event.pointerId) return;
    drag.current = null;
    const card = event.currentTarget;
    if (Math.abs(state.dx) > 90) {
      answerCard(state.dx > 0 ? "present" : "absent");
      return;
    }
    card.removeAttribute("data-dragging");
    card.style.transform = "";
    card.dataset.lean = "";
  }
  // The arrow keys answer the card on a keyboard: right for yes, left for no. Not while the person
  // types in a field, and only while the cards are on screen.
  const cardsOnScreen = step === "take" && mode === "cards" && total > 0;
  useEffect(() => {
    if (!cardsOnScreen) return;
    const onKey = (event: globalThis.KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))) return;
      if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
      event.preventDefault();
      answerCard(event.key === "ArrowRight" ? "present" : "absent");
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  // --- list ----------------------------------------------------------------

  function answerRow(person: AttendancePerson, answer: AttendanceMark) {
    const id = person.bookingId;
    const already = leavingRef.current[id];
    setLeaving({ ...leavingRef.current, [id]: { answer, phase: already?.phase ?? "hit" } });
    // A second tap on a row that is already on its way out only changes its answer: the timers
    // below read the newest one.
    if (already) return;
    later(() => {
      const entry = leavingRef.current[id];
      if (entry) setLeaving({ ...leavingRef.current, [id]: { ...entry, phase: "leave" } });
    }, wait(260));
    later(() => landRow(id), wait(600));
  }

  /** A row that finished sliding out: its answer is stored, and the next step follows. */
  function landRow(id: string) {
    const entry = leavingRef.current[id];
    if (!entry) return;
    const rest = { ...leavingRef.current };
    delete rest[id];
    setLeaving(rest);
    setEdits({ [id]: entry.answer });
    const next = current();
    setBumped((value) => value + 1);
    const person = peopleRef.current.find((row) => row.bookingId === id);
    if (person) setAnnounce(said(person, entry.answer));
    if (!finish(next)) setUndo({ id, answer: entry.answer });
  }

  function putBack(bookingId: string) {
    setEdits({ [bookingId]: null });
    setEntering(bookingId);
    setUndo(null);
    setBumped((value) => value + 1);
    later(() => setEntering((shown) => (shown === bookingId ? null : shown)), wait(750));
  }

  // --- switching, checking, saving -----------------------------------------

  function switchMode(next: AttendanceMode) {
    if (next === mode) return;
    // Rows still sliding out keep their answers: store them now, before the list goes away.
    const pending = leavingRef.current;
    if (Object.keys(pending).length > 0) {
      const flushed: Record<string, AttendanceMark> = {};
      for (const [id, entry] of Object.entries(pending)) flushed[id] = entry.answer;
      setLeaving({});
      setEdits(flushed);
      if (finish(current())) return onModeChange(next);
    }
    setUndo(null);
    if (next === "cards") goTo(Math.max(0, firstOpenIndex(current())), "jump");
    setAnnounce(next === "cards" ? "Cards now. Your answers stay." : "List now. Your answers stay.");
    onModeChange(next);
  }

  function flip(bookingId: string) {
    const now = current();
    setEdits({ [bookingId]: now[bookingId] === "present" ? "absent" : "present" });
    setCelebrate(false);
  }

  async function save() {
    // Only a complete register is saved. The check screen is the only place Save lives, and it
    // goes away when a participant with no answer appears, so this is the last guard.
    const now = current();
    if (!peopleRef.current.every((person) => now[person.bookingId])) return;
    setSaving(true);
    setSaveError(null);
    try {
      setNothingStored((await onSave(now)) === "nothing");
      setSavedAt(formatNow());
      setStep("saved");
    } catch (error) {
      setSaveError(error);
    } finally {
      setSaving(false);
    }
  }

  function backToTake() {
    setCelebrate(false);
    setStep("take");
    if (mode === "cards") goTo(total - 1, "jump");
    else setShowChecked(true);
  }

  // --- render --------------------------------------------------------------

  const live = (
    <p className="sr-only" aria-live="polite">
      {announce}
    </p>
  );

  if (total === 0) {
    return (
      <Frame fill={fillScreen} title={title} subtitle={subtitle} onExit={onExit} exitLabel={exitLabel}>
        {notice}
        <p className="rounded-card border border-line bg-paper p-5 text-[15px] text-ink-2">
          {extras ? "Nobody is booked in this session." : "Nobody is booked in this session. There is no attendance to take."}
        </p>
        {extras ? <NotBooked extras={extras} people={[]} /> : null}
      </Frame>
    );
  }

  if (step === "saved") {
    return (
      <Frame fill={fillScreen} title={title} subtitle={subtitle} onExit={onExit} exitLabel={exitLabel}>
        <div className="flex flex-col items-center gap-3 px-2 pt-10 text-center">
          <span className="att-ring att-pop relative grid size-19 place-items-center rounded-full bg-confirmed text-inverse">
            <CheckIcon size={36} />
            <Burst />
          </span>
          <h2 className="text-[21px]">{nothingStored ? "Nobody came" : `Attendance saved at ${savedAt}`}</h2>
          {nothingStored ? (
            <p className="max-w-[30ch] text-[14px] text-ink-2">Teamna saves nothing for this session. It stays as Not taken yet.</p>
          ) : (
            <p className="max-w-[30ch] text-[14px] text-ink-2">
              {counts.present} came, {counts.absent} absent.
              {counts.absent > 0
                ? ` Absent: ${people
                    .filter((person) => answers[person.bookingId] === "absent")
                    .map((person) => firstName(person.name))
                    .join(", ")}.`
                : ""}
              {notBookedCount > 0 ? ` ${notBookedCount} came with no booking.` : ""}{" "}
              You can change it later.
            </p>
          )}
          <div className="mt-2 flex w-full max-w-[260px] flex-col gap-2">
            <Button fullWidth onClick={onExit}>
              Back to {exitLabel}
            </Button>
            <Button variant="ghost" fullWidth onClick={openCheck}>
              Change
            </Button>
          </div>
        </div>
        {live}
      </Frame>
    );
  }

  if (step === "check") {
    let row = 0;
    const absent = people.filter((person) => answers[person.bookingId] === "absent");
    const came = people.filter((person) => answers[person.bookingId] === "present" && !(extras && person.extraId));
    // Came with no session to pay for it: one red line over the lists (2026-10-29).
    const noneLeft = came.filter((person) => {
      const facts = leftAfter.get(person.bookingId);
      return facts ? sessionsLeftView(facts).over : false;
    });
    const checkRow = (person: AttendancePerson) => (
      <button
        key={person.bookingId}
        type="button"
        onClick={() => flip(person.bookingId)}
        className="att-rise flex min-h-12 w-full items-center gap-3 rounded-xl border border-line bg-paper px-3 py-1.5 text-left active:bg-sunken"
        style={{ "--att-i": row++ } as CSSProperties}
        aria-label={`${person.name}, ${answers[person.bookingId] === "present" ? "came" : "absent"}. Change`}
      >
        <Avatar name={person.name} photoUrl={person.photoUrl ?? null} size={30} />
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span dir="auto" className="truncate text-[15px] font-medium text-ink">
            {person.name}
          </span>
          {leftAfter.has(person.bookingId) ? <SessionsLeftBar facts={leftAfter.get(person.bookingId)!} size="mini" /> : null}
        </span>
        <AnswerTag answer={answers[person.bookingId]} />
        <span className="text-[12px] font-semibold text-accent-strong">Change</span>
      </button>
    );
    return (
      <Frame fill={fillScreen} title="Check before you save" subtitle={title} onExit={backToTake} exitLabel="Back">
        <div className="flex flex-col gap-2 pb-4">
          <div className={`notice notice-success relative flex flex-col items-center gap-1 rounded-card px-4 py-4 text-center ${celebrate ? "att-pop" : ""}`}>
            {celebrate ? <Burst /> : null}
            <NoticeCoin tone="success" />
            <p className="text-[17px] font-bold">Everyone is checked</p>
            <p className="text-[13px] opacity-90">
              All {people1(total)} have an answer. Tap a name to change it.
            </p>
            <p className="mt-1 flex gap-1.5">
              <span className="rounded-full bg-paper px-2.5 py-0.5 text-[12px] font-bold text-confirmed-ink">{counts.present} came</span>
              <span className="rounded-full bg-paper px-2.5 py-0.5 text-[12px] font-bold text-cancelled-ink">{counts.absent} absent</span>
            </p>
          </div>
          {noneLeft.length ? (
            <div className="flex flex-col gap-0.5 rounded-xl border border-cancelled/30 bg-cancelled-surface px-3 py-2.5 text-[13px] text-ink">
              <p className="font-bold text-cancelled-ink">
                {noneLeft.length} came with no sessions left
              </p>
              <p dir="auto">
                {noneLeft.map((person) => person.name).join(", ")}. A later package or paid month that covers this class pays for {noneLeft.length === 1 ? "this session" : "these sessions"}.
              </p>
            </div>
          ) : null}
          {absent.length ? <h2 className="px-1 pt-2 text-[13px] font-semibold text-ink-2">Absent ({absent.length})</h2> : null}
          {absent.map(checkRow)}
          {came.length ? <h2 className="px-1 pt-2 text-[13px] font-semibold text-ink-2">Came ({came.length})</h2> : null}
          {came.map(checkRow)}
          {extras ? <NotBooked extras={extras} people={people.filter((person) => person.extraId)} /> : null}
          {saveError ? <InlineError error={saveError} /> : null}
        </div>
        <Footer>
          <Button fullWidth size="lg" loading={saving} onClick={() => void save()}>
            Save attendance
          </Button>
        </Footer>
        {live}
      </Frame>
    );
  }

  // step === "take"
  const switcher = checkIn ? (
    <SegmentedControl<View>
      label="How to take attendance"
      size="compact"
      options={[{ value: "checkin", label: "Check-in" }, ...attendanceModes.map((value) => ({ value, label: value === "cards" ? "Cards" : "List" }))]}
      value={checkingIn ? "checkin" : (mode ?? "cards")}
      onChange={(next) => {
        if (next === "checkin") {
          setUndo(null);
          setCheckingIn(true);
          setAnnounce("Check-in now.");
          return;
        }
        setCheckingIn(false);
        if (next === mode) return;
        if (!mode) onModeChange(next);
        else switchMode(next);
      }}
    />
  ) : mode ? (
    <SegmentedControl<AttendanceMode>
      label="How to take attendance"
      size="compact"
      options={attendanceModes.map((value) => ({ value, label: value === "cards" ? "Cards" : "List" }))}
      value={mode}
      onChange={switchMode}
    />
  ) : null;

  if (checkIn && checkingIn) {
    return (
      <Frame fill={fillScreen} title={title} subtitle={subtitle} onExit={onExit} exitLabel={exitLabel} action={switcher}>
        {notice}
        <CheckInList {...checkIn} people={people.filter((person) => !person.notBooked)} participantLabel={participantLabel} />
        {extras ? <SomeoneElseButton onClick={extras.onAdd} waiting={extras.waiting.length} /> : null}
        {live}
      </Frame>
    );
  }

  if (!mode) {
    return (
      <Frame fill={fillScreen} title={title} subtitle={subtitle} onExit={onExit} exitLabel={exitLabel}>
        <div className="att-pop flex flex-col gap-3 rounded-card border border-line bg-paper p-4">
          <h2 className="text-[19px]">How do you want to take attendance?</h2>
          <p className="-mt-1 text-[13px] text-ink-2">You can change this at any time at the top of the screen.</p>
          <ChoiceButton
            picture={<CardsPicture />}
            title="One card at a time"
            text={`Yes or No for each ${participantLabel.one}. The next card comes by itself.`}
            onClick={() => onModeChange("cards")}
          />
          <ChoiceButton
            picture={<ListPicture />}
            title="A list"
            text="Yes or No next to each name. Answered names move to Checked."
            onClick={() => onModeChange("list")}
          />
        </div>
      </Frame>
    );
  }

  if (mode === "cards") {
    const person = people[Math.min(index, total - 1)]!;
    const answer = answers[person.bookingId];
    return (
      <Frame fill={fillScreen} title={title} subtitle={subtitle} onExit={onExit} exitLabel={exitLabel} action={switcher}>
        <div className="flex min-h-[calc(100dvh-170px)] flex-col">
          {notice}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-baseline justify-between text-[13px] text-ink-2">
              <span key={`n-${cardKey}`} className="att-bump text-[15px] font-bold text-ink">
                {index + 1} of {total}
              </span>
              <span>
                {counts.present} came, {counts.absent} absent
              </span>
            </div>
            <div className="flex gap-[3px]">
              {people.map((entry, position) => {
                const value = answers[entry.bookingId];
                return (
                  <button
                    key={entry.bookingId}
                    type="button"
                    onClick={() => position !== index && goTo(position, position < index ? "prev" : "fwd")}
                    aria-label={`${entry.name}, ${value === "present" ? "came" : value === "absent" ? "absent" : "not checked"}`}
                    aria-current={position === index ? "step" : undefined}
                    className={`relative h-2 flex-1 rounded-full transition-colors ${value === "present" ? "bg-confirmed" : value === "absent" ? "bg-cancelled" : "bg-line"} ${position === poppedStep ? "att-seg-pop" : ""}`}
                  >
                    {position === index ? <span className="absolute left-1/2 top-3 size-1.5 -translate-x-1/2 rounded-full bg-ink" /> : null}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center justify-between pt-1">
              <Button variant="ghost" className="min-h-9 px-1 text-[14px]" disabled={index === 0} onClick={() => goTo(index - 1, "prev")}>
                <ArrowLeftIcon size={16} />
                Back
              </Button>
              {counts.done === total ? (
                <Button variant="ghost" className="min-h-9 px-1 text-[14px]" onClick={openCheck}>
                  Check the list
                </Button>
              ) : null}
              <Button variant="ghost" className="min-h-9 px-1 text-[14px]" disabled={index === total - 1} onClick={() => goTo(index + 1, "fwd")}>
                Next
                <ArrowLeftIcon size={16} className="rotate-180" />
              </Button>
            </div>
          </div>

          <div className="att-stack mx-1 mt-3 min-h-[300px] flex-1">
            {index < total - 2 ? <div className={`att-card-peek att-card-peek-2 rounded-[26px] border border-line bg-paper ${enter === "next" ? "att-grow" : ""}`} key={`p2-${cardKey}`} /> : null}
            {index < total - 1 ? <div className={`att-card-peek att-card-peek-1 rounded-[26px] border border-line bg-paper ${enter === "next" ? "att-grow" : ""}`} key={`p1-${cardKey}`} /> : null}
            <div
              key={`card-${cardKey}`}
              ref={cardRef}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerEnd}
              onPointerCancel={onPointerEnd}
              className={`att-card att-in-${enter} flex flex-col items-center justify-center gap-2 rounded-[26px] border border-line bg-paper px-5 py-6 text-center shadow-[0_2px_4px_rgba(17,26,43,0.06),0_18px_36px_-18px_rgba(17,26,43,0.35)]`}
            >
              <span className="att-stamp att-stamp-present absolute left-5 top-5 -rotate-12 rounded-lg border-[3px] border-confirmed px-2.5 py-1 text-[17px] font-extrabold tracking-wider text-confirmed">
                CAME
              </span>
              <span className="att-stamp att-stamp-absent absolute right-5 top-5 rotate-12 rounded-lg border-[3px] border-cancelled px-2.5 py-1 text-[17px] font-extrabold tracking-wider text-cancelled">
                ABSENT
              </span>
              <Avatar name={person.name} photoUrl={person.photoUrl ?? null} size={96} />
              <p dir="auto" className="text-[24px] font-bold text-ink">
                {person.name}
              </p>
              {person.detail ? <p className="-mt-1 text-[13px] text-ink-2">{person.detail}</p> : null}
              {leftAfter.has(person.bookingId) ? (
                <div className="flex w-full justify-center">
                  <SessionsLeftBar facts={leftAfter.get(person.bookingId)!} size="card" />
                </div>
              ) : null}
              {person.firstClass ? <Badge tone="info">{FIRST_CLASS_LABEL}</Badge> : null}
              {person.note ? (
                <span dir="auto" className="rounded-full bg-late-surface px-2.5 py-0.5 text-[12px] font-semibold text-late-ink">
                  {person.note}
                </span>
              ) : null}
              {person.healthNote ? <HealthNoteBadge /> : null}
              <p className="mt-2 text-[15px] font-semibold text-ink">Did {firstName(person.name)} come today?</p>
              {answer ? (
                <p className="flex items-center gap-1.5 text-[13px] text-ink-2">
                  You said <AnswerTag answer={answer} />
                </p>
              ) : null}
            </div>
          </div>

          <div className="flex justify-center gap-10 pb-2 pt-5">
            <AnswerButton answer="absent" chosen={answer === "absent"} onClick={() => answerCard("absent")} name={person.name} />
            <AnswerButton answer="present" chosen={answer === "present"} onClick={() => answerCard("present")} name={person.name} />
          </div>
          <p className="pb-2 text-center text-[12px] text-ink-2">Swipe right for yes, left for no</p>
          {extras ? <SomeoneElseButton onClick={extras.onAdd} waiting={extras.waiting.length} /> : null}
        </div>
        {live}
      </Frame>
    );
  }

  // mode === "list"
  const pending = people.filter((person) => !answers[person.bookingId]);
  const checked = people.filter((person) => answers[person.bookingId]);
  const toCheck = total - counts.done;
  const undoPerson = undo ? people.find((person) => person.bookingId === undo.id) : undefined;
  return (
    <Frame fill={fillScreen} title={title} subtitle={subtitle} onExit={onExit} exitLabel={exitLabel} action={switcher}>
      <div className="flex flex-col gap-2 pb-4">
        {notice}
        <div className="flex flex-col gap-1.5">
          <div className="flex items-baseline justify-between text-[13px] text-ink-2">
            <span>
              <span key={`b-${bumped}`} className="att-bump text-[15px] font-bold text-ink">
                {toCheck === 0 ? "All checked" : `${toCheck} to check`}
              </span>
            </span>
            <span>
              {counts.present} came, {counts.absent} absent
            </span>
          </div>
          <div className="att-track flex h-2 overflow-hidden rounded-full bg-line" aria-hidden="true">
            <span className="h-full bg-confirmed" style={{ width: `${(counts.present / total) * 100}%` }} />
            <span className="h-full bg-cancelled" style={{ width: `${(counts.absent / total) * 100}%` }} />
          </div>
        </div>

        {pending.length ? (
          <div className="flex justify-between px-1 pt-1 text-[12px] font-semibold text-ink-2">
            <span>{participantLabel.one.charAt(0).toUpperCase() + participantLabel.one.slice(1)}</span>
            <span>Did they come?</span>
          </div>
        ) : null}
        {pending.map((person) => {
          const state = leaving[person.bookingId] ?? null;
          return (
            <div
              key={person.bookingId}
              data-hit={state?.answer}
              data-leave={state?.phase === "leave" ? state.answer : undefined}
              className={`att-row relative flex min-h-15 items-center gap-2.5 rounded-xl border border-line bg-paper px-2.5 py-2 ${entering === person.bookingId ? "att-row-enter" : ""}`}
            >
              <Avatar name={person.name} photoUrl={person.photoUrl ?? null} size={38} />
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span dir="auto" className="truncate text-[15px] font-semibold text-ink">
                  {person.name}
                </span>
                {person.detail ? <span className="text-[12px] leading-snug text-ink-2">{person.detail}</span> : null}
                {leftAfter.has(person.bookingId) ? <SessionsLeftBar facts={leftAfter.get(person.bookingId)!} /> : null}
                {person.firstClass ? (
                  <span className="self-start">
                    <Badge tone="info">{FIRST_CLASS_LABEL}</Badge>
                  </span>
                ) : null}
                {person.note ? (
                  <span dir="auto" className="self-start rounded-full bg-late-surface px-2 py-px text-[11.5px] font-semibold text-late-ink">
                    {person.note}
                  </span>
                ) : null}
                {person.healthNote ? (
                  <span className="self-start">
                    <HealthNoteBadge />
                  </span>
                ) : null}
              </div>
              <div className="att-answers flex shrink-0 gap-1.5">
                <Button
                  variant="secondary"
                  className="w-12 flex-col gap-0 px-0 text-[10px]"
                  aria-label={`${firstName(person.name)} did not come`}
                  onClick={() => answerRow(person, "absent")}
                >
                  <CloseIcon size={17} className="text-cancelled" />
                  No
                </Button>
                <Button
                  variant="secondary"
                  className="w-12 flex-col gap-0 px-0 text-[10px]"
                  aria-label={`${firstName(person.name)} came`}
                  onClick={() => answerRow(person, "present")}
                >
                  <CheckIcon size={17} className="text-confirmed" />
                  Yes
                </Button>
              </div>
              <span className="att-hitmark att-hitmark-present absolute right-3 top-1/2 -mt-[15px] h-[30px] items-center gap-1 rounded-full bg-confirmed px-3 text-[13px] font-bold text-inverse">
                <CheckIcon size={14} /> Came
              </span>
              <span className="att-hitmark att-hitmark-absent absolute right-3 top-1/2 -mt-[15px] h-[30px] items-center gap-1 rounded-full bg-cancelled px-3 text-[13px] font-bold text-inverse">
                <CloseIcon size={14} /> Absent
              </span>
            </div>
          );
        })}

        {checked.length ? (
          <>
            <button
              type="button"
              aria-expanded={showChecked || pending.length === 0}
              onClick={() => setShowChecked((open) => !open)}
              className="mt-1 flex min-h-11 w-full items-center justify-between border-t border-line px-1 text-[14px] font-semibold text-ink-2"
            >
              <span>
                Checked (<span key={`c-${bumped}`} className="att-bump">{checked.length}</span>)
              </span>
              <span className="text-accent-strong">{showChecked || pending.length === 0 ? "Hide" : "Show"}</span>
            </button>
            {showChecked || pending.length === 0
              ? checked.map((person) => (
                  <div key={person.bookingId} className="flex min-h-12 items-center gap-2.5 border-b border-line px-1 py-1.5">
                    <Avatar name={person.name} photoUrl={person.photoUrl ?? null} size={28} />
                    <span dir="auto" className="min-w-0 flex-1 truncate text-[14px] text-ink">
                      {person.name}
                    </span>
                    <AnswerTag answer={answers[person.bookingId]} />
                    <Button variant="ghost" className="min-h-9 px-2 text-[13px]" onClick={() => putBack(person.bookingId)}>
                      Change
                    </Button>
                  </div>
                ))
              : null}
          </>
        ) : null}
        {extras ? <SomeoneElseButton onClick={extras.onAdd} waiting={extras.waiting.length} /> : null}
      </div>
      <Footer>
        <Button fullWidth size="lg" disabled={toCheck > 0} onClick={openCheck}>
          {toCheck > 0 ? `${toCheck} to check` : "Check and save"}
        </Button>
      </Footer>
      {undo && undoPerson ? (
        <output className="notice notice-info notice-float fixed inset-x-4 bottom-36 z-30 mx-auto flex max-w-[400px] items-center gap-3 rounded-card px-4 py-3">
          <span className="min-w-0 flex-1 text-[14px] font-semibold">{said(undoPerson, undo.answer)}</span>
          <Button variant="ghost" className="min-h-9 px-2" onClick={() => putBack(undo.id)}>
            Undo
          </Button>
        </output>
      ) : null}
      {live}
    </Frame>
  );
}

/** The page frame: back link, title, subtitle, and the switch on the title's line. */
function Frame({
  fill,
  title,
  subtitle,
  onExit,
  exitLabel,
  action,
  children,
}: {
  title: string;
  subtitle: string;
  onExit: () => void;
  exitLabel: string;
  action?: ReactNode;
  children: ReactNode;
  fill: boolean;
}) {
  return (
    // A column at least the height of the screen, so the save bar sits at the bottom even when the
    // list is short (found on the staging walk: it floated under the last row).
    <div className={`mx-auto flex w-full max-w-app flex-col px-5 ${fill ? "min-h-dvh" : ""}`} data-fill={fill ? "screen" : "content"}>
      <header className="pt-safe">
        <div className="mt-4 flex items-center justify-between gap-3">
          <button type="button" onClick={onExit} className="-ml-1 inline-flex min-h-11 items-center gap-1.5 pr-2 text-[14px] font-semibold text-accent-strong">
            <ArrowLeftIcon size={18} />
            {exitLabel}
          </button>
          {action ? <div className="shrink-0">{action}</div> : null}
        </div>
        <div className="pb-4 pt-1">
          <h1 className="text-[24px] leading-tight">{title}</h1>
          <p className="text-[13px] text-ink-2">{subtitle}</p>
        </div>
      </header>
      {children}
    </div>
  );
}

/** The action bar pinned to the bottom of the screen, where the thumb already is. */
function Footer({ children }: { children: ReactNode }) {
  return (
    <div className="sticky bottom-0 z-20 -mx-5 mt-auto border-t border-line bg-paper/95 px-5 pb-[max(12px,env(safe-area-inset-bottom))] pt-3 backdrop-blur">
      {children}
    </div>
  );
}

/** "Someone else came": opens the search. A count shows names that came and wait for a parent. */
function SomeoneElseButton({ onClick, waiting }: { onClick: () => void; waiting: number }) {
  return (
    <div className="flex flex-col gap-1 pt-3">
      <Button variant="secondary" fullWidth onClick={onClick}>
        Someone else came
      </Button>
      {waiting > 0 ? <p className="text-center text-[12.5px] text-ink-2">{waiting === 1 ? "1 name came" : `${waiting} names came`} and waits for the owner.</p> : null}
    </div>
  );
}

/**
 * On the check screen: the swimmers who came and were not booked. Swimmers of the club (a walk-in
 * booking, Came) and names that wait for a parent, each with Remove, then the add button.
 */
function NotBooked({ extras, people }: { extras: AttendanceExtras; people: AttendancePerson[] }) {
  const rows = [
    ...people.map((person) => ({ id: person.extraId!, name: person.name, photoUrl: person.photoUrl ?? null, sub: "Not booked" })),
    ...extras.waiting.map((name) => ({ id: name.id, name: name.name, photoUrl: null, sub: "New. Not booked. Waits for the owner" })),
  ];
  return (
    <>
      {rows.length ? <h2 className="px-1 pt-2 text-[13px] font-semibold text-ink-2">Came, not booked ({rows.length})</h2> : null}
      {rows.map((row) => (
        <div key={row.id} className="flex min-h-12 items-center gap-3 rounded-xl border border-line bg-paper px-3 py-1.5">
          <Avatar name={row.name} photoUrl={row.photoUrl} size={30} />
          <span className="min-w-0 flex-1">
            <span dir="auto" className="block truncate text-[15px] font-medium text-ink">
              {row.name}
            </span>
            <span className="block text-[12px] text-ink-2">{row.sub}</span>
          </span>
          <AnswerTag answer="present" />
          <Button variant="ghost" className="min-h-9 px-2 text-[13px]" aria-label={`Remove ${row.name}`} onClick={() => extras.onRemove(row.id)}>
            Remove
          </Button>
        </div>
      ))}
      <SomeoneElseButton onClick={extras.onAdd} waiting={0} />
      {rows.length ? (
        <p className="px-1 text-[12.5px] text-ink-2">
          Came takes 1 session from their package. With none left, a later package or paid month that covers this class pays for it.
        </p>
      ) : null}
    </>
  );
}

function AnswerButton({ answer, chosen, onClick, name }: { answer: AttendanceMark; chosen: boolean; onClick: () => void; name: string }) {
  const [pressed, setPressed] = useState(0);
  const yes = answer === "present";
  return (
    <Button
      key={pressed}
      variant={yes ? "confirmed" : "danger"}
      aria-pressed={chosen}
      aria-label={yes ? `Yes, ${firstName(name)} came` : `No, ${firstName(name)} did not come`}
      className={`size-[72px] flex-col gap-0 !rounded-full px-0 text-[11px] ${pressed ? "att-press" : ""} ${chosen ? "outline-3 outline-offset-3 outline-ink/25" : ""}`}
      onClick={() => {
        setPressed((value) => value + 1);
        onClick();
      }}
    >
      {yes ? <CheckIcon size={26} /> : <CloseIcon size={26} />}
      {yes ? "Yes" : "No"}
    </Button>
  );
}

function ChoiceButton({ picture, title, text, onClick }: { picture: ReactNode; title: string; text: string; onClick: () => void }) {
  return (
    <Button variant="secondary" fullWidth onClick={onClick} className="h-auto justify-start gap-3 py-3 text-left">
      {picture}
      <span className="flex min-w-0 flex-col">
        <span className="text-[15px] font-bold text-ink">{title}</span>
        <span className="text-[12.5px] font-normal text-ink-2">{text}</span>
      </span>
    </Button>
  );
}

function CardsPicture() {
  return (
    <span aria-hidden="true" className="relative block h-16 w-[74px] shrink-0 overflow-hidden rounded-xl bg-accent-surface">
      <span className="absolute left-1/2 top-3.5 h-11 w-9 -translate-x-1/2 scale-[0.86] rounded-lg border border-line-strong bg-paper opacity-60" />
      <span className="absolute left-1/2 top-2.5 h-11 w-9 -translate-x-1/2 -rotate-8 rounded-lg border border-line-strong bg-paper shadow-sm">
        <span className="absolute left-2.5 top-2 size-4 rounded-full bg-late-surface" />
      </span>
    </span>
  );
}

function ListPicture() {
  return (
    <span aria-hidden="true" className="relative flex h-16 w-[74px] shrink-0 flex-col justify-center gap-1.5 rounded-xl bg-accent-surface px-2.5">
      {[
        ["top", "bg-confirmed"],
        ["middle", "bg-cancelled"],
        ["bottom", "bg-confirmed"],
      ].map(([key, tone]) => (
        <span key={key} className="relative block h-2.5 rounded bg-paper">
          <span className={`absolute right-0.5 top-0.5 h-1.5 w-3 rounded-sm ${tone}`} />
        </span>
      ))}
    </span>
  );
}
